/**
 * RTWO Cryptography & Identity Engine — Provider Registries
 */

import type {
  IIdentityBackend,
  IIdentityBackendRegistry,
  IdentityBackendStatus,
  IdentityBackendType,
} from '../adapters/identity-backend-interface';
import { NullIdentityBackend } from '../adapters/null-identity-backend';
import type {
  IKeyProvider,
  IKeyProviderRegistry,
  KeyProviderStatus,
  KeyProviderType,
} from '../adapters/key-provider-interface';
import { NullKeyProvider } from '../adapters/null-key-provider';

export class KeyProviderRegistry implements IKeyProviderRegistry {
  private activeProvider: IKeyProvider = new NullKeyProvider();
  private readonly providers = new Map<string, IKeyProvider>();

  constructor() {
    this.providers.set(this.activeProvider.providerId, this.activeProvider);
  }

  register(provider: IKeyProvider, options?: { activate?: boolean }): void {
    this.providers.set(provider.providerId, provider);
    const shouldActivate = options?.activate !== false;
    if (shouldActivate && provider.providerType !== 'not_configured') {
      this.activeProvider = provider;
    }
  }

  setActiveProvider(providerId: string): boolean {
    const provider = this.providers.get(providerId);
    if (!provider || provider.providerType === 'not_configured') {
      return false;
    }
    this.activeProvider = provider;
    return true;
  }

  unregister(providerId: string): boolean {
    if (providerId === 'null-key-provider') {
      return false;
    }
    const removed = this.providers.delete(providerId);
    if (this.activeProvider.providerId === providerId) {
      this.activeProvider = new NullKeyProvider();
      this.providers.set(this.activeProvider.providerId, this.activeProvider);
    }
    return removed;
  }

  getActiveProvider(): IKeyProvider {
    return this.activeProvider;
  }

  getProvider(providerId: string): IKeyProvider | null {
    return this.providers.get(providerId) ?? null;
  }

  getProviderByType(providerType: KeyProviderType): IKeyProvider | null {
    for (const provider of this.providers.values()) {
      if (provider.providerType === providerType) {
        return provider;
      }
    }
    return null;
  }

  getAllProviders(): IKeyProvider[] {
    return Array.from(this.providers.values());
  }

  isConfigured(): boolean {
    return (
      this.activeProvider.providerType !== 'not_configured' &&
      this.activeProvider.providerId !== 'null-key-provider'
    );
  }

  getStatus(): KeyProviderStatus {
    return this.activeProvider.getStatus();
  }
}

export class IdentityBackendRegistry implements IIdentityBackendRegistry {
  private activeBackend: IIdentityBackend = new NullIdentityBackend();
  private readonly backends = new Map<string, IIdentityBackend>();

  constructor() {
    this.backends.set(this.activeBackend.backendId, this.activeBackend);
  }

  register(backend: IIdentityBackend, options?: { activate?: boolean }): void {
    this.backends.set(backend.backendId, backend);
    const shouldActivate = options?.activate !== false;
    if (shouldActivate && backend.backendType !== 'not_configured') {
      this.activeBackend = backend;
    }
  }

  setActiveBackend(backendId: string): boolean {
    const backend = this.backends.get(backendId);
    if (!backend || backend.backendType === 'not_configured') {
      return false;
    }
    this.activeBackend = backend;
    return true;
  }

  unregister(backendId: string): boolean {
    if (backendId === 'null-identity-backend') {
      return false;
    }
    const removed = this.backends.delete(backendId);
    if (this.activeBackend.backendId === backendId) {
      this.activeBackend = new NullIdentityBackend();
      this.backends.set(this.activeBackend.backendId, this.activeBackend);
    }
    return removed;
  }

  getActiveBackend(): IIdentityBackend {
    return this.activeBackend;
  }

  getBackend(backendId: string): IIdentityBackend | null {
    return this.backends.get(backendId) ?? null;
  }

  getBackendByType(backendType: IdentityBackendType): IIdentityBackend | null {
    for (const backend of this.backends.values()) {
      if (backend.backendType === backendType) {
        return backend;
      }
    }
    return null;
  }

  getAllBackends(): IIdentityBackend[] {
    return Array.from(this.backends.values());
  }

  isConfigured(): boolean {
    return (
      this.activeBackend.backendType !== 'not_configured' &&
      this.activeBackend.backendId !== 'null-identity-backend'
    );
  }

  getStatus(): IdentityBackendStatus {
    return this.activeBackend.getStatus();
  }
}

let _keyProviderRegistry: KeyProviderRegistry | null = null;
let _identityBackendRegistry: IdentityBackendRegistry | null = null;

export function getKeyProviderRegistry(): KeyProviderRegistry {
  if (!_keyProviderRegistry) {
    _keyProviderRegistry = new KeyProviderRegistry();
  }
  return _keyProviderRegistry;
}

export function getIdentityBackendRegistry(): IdentityBackendRegistry {
  if (!_identityBackendRegistry) {
    _identityBackendRegistry = new IdentityBackendRegistry();
  }
  return _identityBackendRegistry;
}

export const keyProviderRegistry = getKeyProviderRegistry();
export const identityBackendRegistry = getIdentityBackendRegistry();
